import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  User, 
  Plane,
  FileText,
  Settings,
  Ship,
  Clock,
  Info,
  Plus,
  Edit,
  Save,
  X,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TripView = () => {
  const { tripId } = useParams();
  const { user } = useAuth();
  const [tripData, setTripData] = useState(null);
  const [itineraries, setItineraries] = useState([]);
  const [cruiseInfo, setCruiseInfo] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({ day_number: 1, note_text: '' });
  const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  // State for trip details forms (using simple approach)
  const [tripDetailsForm, setTripDetailsForm] = useState({
    ship_name: '',
    boarding_port: '',
    cabin_number: '',
    package_type: '',
    insurance_type: '',
    restaurant: '',
    dinner_time: '',
    resort_name: '',
    room_type: '',
    meal_plan: '',
    package_formula: '',
    general_info: '',
    custom_details: ''
  });

  useEffect(() => {
    if (tripId) {
      fetchTripData();
    }
  }, [tripId]);

  // Effetto per ricaricare quando cambia tripId o quando si rientra nella tab
  useEffect(() => {
    if (tripId && user) {
      fetchTripData();
    }
  }, [tripId, user]);

  const fetchTripData = async () => {
    try {
      const requests = [
        axios.get(`${API}/trips/${tripId}/full`),
        axios.get(`${API}/trips/${tripId}/itineraries`),
      ];

      // Cruise info per admin/agent
      if (user.role !== 'client') {
        requests.push(axios.get(`${API}/trips/${tripId}/cruise-info`));
      }

      // Note per tutti i ruoli (clienti vedono le proprie, admin/agent tutte)
      requests.push(axios.get(`${API}/trips/${tripId}/notes`));

      const responses = await Promise.all(requests);
      
      setTripData(responses[0].data);
      setItineraries(responses[1].data);
      
      let responseIndex = 2;
      if (user.role !== 'client') {
        const cruiseData = responses[responseIndex].data;
        setCruiseInfo(cruiseData);
        responseIndex++;
      }
      
      // Note sono sempre l'ultimo elemento
      const notesData = responses[responseIndex].data || [];
      setNotes(notesData);
      
      // Cerco i dettagli del viaggio nelle note speciali
      console.log('🔍 Cerco dettagli nelle note:', notesData);
      const tripDetailsNote = notesData.find(note => 
        note.note_text && note.note_text.startsWith('TRIP_DETAILS:')
      );
      
      if (tripDetailsNote) {
        try {
          const jsonData = tripDetailsNote.note_text.replace('TRIP_DETAILS:', '');
          const formData = JSON.parse(jsonData);
          console.log('✅ Caricati dettagli dalle note:', formData);
          setTripDetailsForm(formData);
        } catch (e) {
          console.error('❌ Errore parsing dettagli dalle note:', e);
        }
      } else {
        console.log('🔍 Nessun dettaglio salvato nelle note, form vuoto');
        // Form vuoto per permettere di iniziare a compilare
        setTripDetailsForm({
          ship_name: '',
          boarding_port: '',
          cabin_number: '',
          package_type: '',
          insurance_type: '',
          restaurant: '',
          dinner_time: '',
          resort_name: '',
          room_type: '',
          meal_plan: '',
          package_formula: '',
          general_info: '',
          custom_details: ''
        });
      }
      
    } catch (error) {
      console.error('Error fetching trip data:', error);
      toast.error('Errore nel caricamento del viaggio');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async (noteData) => {
    try {
      if (editingNote) {
        // Update existing note
        await axios.put(`${API}/notes/${editingNote.id}`, { 
          note_text: noteData.note_text 
        });
        toast.success('Nota aggiornata con successo!');
      } else {
        // Create new note
        await axios.post(`${API}/trips/${tripId}/notes`, {
          day_number: noteData.day_number,
          note_text: noteData.note_text,
          trip_id: tripId
        });
        toast.success('Nota creata con successo!');
      }
      
      setEditingNote(null);
      setNewNote({ day_number: 1, note_text: '' });
      setShowNewNoteDialog(false);
      fetchTripData();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Errore nel salvataggio della nota');
    }
  };

  const handleStatusChange = async () => {
    try {
      await axios.put(`${API}/trips/${tripId}/status`, { status: newStatus });
      toast.success(`Stato viaggio aggiornato a: ${newStatus}`);
      
      setShowStatusDialog(false);
      setNewStatus('');
      fetchTripData(); // Reload trip data to show new status
    } catch (error) {
      console.error('Error updating trip status:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nell\'aggiornamento dello stato';
      toast.error(errorMessage);
    }
  };

  const openStatusDialog = () => {
    setNewStatus(tripData.trip.status);
    setShowStatusDialog(true);
  };

  const handleSaveTripDetails = async () => {
    console.log('🔍 SALVATAGGIO INVIATO - Form data:', tripDetailsForm);
    console.log('🔍 Trip ID:', tripId);
    
    try {
      // Salvo i dati come nota speciale del viaggio per mantenerli nella tab Dettagli
      const formDataJson = JSON.stringify(tripDetailsForm);
      
      // Prima verifico se esiste già una nota con i dettagli
      const existingNote = notes.find(note => 
        note.note_text && note.note_text.startsWith('TRIP_DETAILS:')
      );
      
      if (existingNote) {
        // Aggiorno la nota esistente
        console.log('🔍 Aggiornando nota esistente:', existingNote.id);
        const updatePayload = {
          note_text: `TRIP_DETAILS:${formDataJson}`
        };
        
        const response = await axios.put(`${API}/notes/${existingNote.id}`, updatePayload);
        console.log('🎉 NOTA AGGIORNATA:', response.data);
      } else {
        // Creo una nuova nota
        console.log('🔍 Creando nuova nota per dettagli');
        const notePayload = {
          trip_id: tripId,
          day_number: 0, // Uso day 0 per indicare che sono dettagli generali
          note_text: `TRIP_DETAILS:${formDataJson}`, // Prefisso speciale per identificare i dettagli
          user_id: user.id
        };
        
        const response = await axios.post(`${API}/trips/${tripId}/notes`, notePayload);
        console.log('🎉 NUOVA NOTA CREATA:', response.data);
      }
      
      toast.success('Dettagli viaggio salvati con successo!');
      
      // Ricarico le note per aggiornare la lista
      const notesResponse = await axios.get(`${API}/trips/${tripId}/notes`);
      setNotes(notesResponse.data || []);
      
    } catch (error) {
      console.error('❌ ERRORE SALVATAGGIO:', error);
      console.error('❌ DETTAGLI ERRORE:', error.response?.data);
      toast.error('Errore nel salvataggio dei dettagli');
    }
  };

  const getGridColsClass = () => {
    const totalTabs = tabsToShow.length + (user.role !== 'client' ? 2 : 0); // +2 for client-notes and trip-details
    if (totalTabs <= 2) return 'grid-cols-2';
    if (totalTabs <= 3) return 'grid-cols-3';
    if (totalTabs <= 4) return 'grid-cols-4';
    if (totalTabs <= 5) return 'grid-cols-5';
    return 'grid-cols-6';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTripTypeColor = (type) => {
    switch (type) {
      case 'cruise': return 'bg-blue-100 text-blue-800';
      case 'resort': return 'bg-green-100 text-green-800';
      case 'tour': return 'bg-purple-100 text-purple-800';
      case 'custom': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!tripData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Viaggio non trovato</h2>
          <Link to="/dashboard">
            <Button>Torna alla Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { trip, agent, client } = tripData;

  // Determina quanti tab mostrare
  const tabsToShow = [];
  
  tabsToShow.push('itinerary');
  
  if (trip.trip_type === 'cruise' && cruiseInfo) {
    tabsToShow.push('cruise');
  }
  
  if (user.role === 'client') {
    tabsToShow.push('notes');
    tabsToShow.push('trip-details'); // Clienti possono vedere i dettagli compilati dagli agenti
  }
  
  if (user.role !== 'client') {
    // Admin e Agent possono modificare i dettagli del viaggio
    tabsToShow.push('trip-details');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-0 sm:h-16 gap-4 sm:gap-0">
            <div className="flex items-center">
              <Link to={user.role === 'client' ? '/dashboard' : '/manage-trips'}>
                <Button variant="ghost" size="sm" className="mr-4">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <img 
                src="https://customer-assets.emergentagent.com/job_e899d787-232a-49fb-887e-eb0377fd3380/artifacts/dy8a9fcf_trib%C3%B9%20in%20viaggio.png" 
                alt="Tribù in Viaggio" 
                className="h-10 sm:h-12 w-auto mr-3"
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{trip.title}</h1>
                <p className="text-xs sm:text-sm text-slate-600 truncate">{trip.destination}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${getStatusColor(trip.status)} text-xs sm:text-sm`}>
                  {trip.status}
                </Badge>
                <Badge className={`${getTripTypeColor(trip.trip_type)} text-xs sm:text-sm`}>
                  {trip.trip_type}
                </Badge>
                {user.role !== 'client' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={openStatusDialog}
                    className="flex items-center gap-2 text-xs sm:text-sm px-2 sm:px-3"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Cambia Stato</span>
                    <span className="sm:hidden">Stato</span>
                  </Button>
                )}
              </div>
              
              {user.role !== 'client' && (
                <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                  <Link to={`/trip-admin/${tripId}`} className="flex-1 sm:flex-initial">
                    <Button variant="outline" size="sm" className="flex items-center justify-center gap-2 w-full text-xs sm:text-sm px-2 sm:px-3">
                      <Settings className="w-4 h-4" />
                      <span className="hidden sm:inline">Amministrazione</span>
                      <span className="sm:hidden">Admin</span>
                    </Button>
                  </Link>
                  <Link to={`/trips/${tripId}/itinerary`} className="flex-1 sm:flex-initial">
                    <Button variant="outline" size="sm" className="flex items-center justify-center gap-2 w-full text-xs sm:text-sm px-2 sm:px-3">
                      <Edit className="w-4 h-4" />
                      <span className="hidden sm:inline">Modifica Itinerario</span>
                      <span className="sm:hidden">Itinerario</span>
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trip Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Plane className="w-6 h-6 text-teal-600" />
              Dettagli Viaggio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-600">
                  <MapPin className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Destinazione</p>
                    <p className="text-sm">{trip.destination}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-slate-600">
                  <Calendar className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Date</p>
                    <p className="text-sm">
                      {new Date(trip.start_date).toLocaleDateString('it-IT')} - 
                      {new Date(trip.end_date).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              </div>

              {client && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <User className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Cliente</p>
                      <p className="text-sm">{client.first_name} {client.last_name}</p>
                      <p className="text-xs text-slate-500">{client.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {agent && user.role === 'client' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <User className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Il tuo Agente</p>
                      <p className="text-sm">{agent.first_name} {agent.last_name}</p>
                      <p className="text-xs text-slate-500">{agent.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="font-medium text-slate-800 mb-2">Descrizione</h4>
              <p className="text-slate-600">{trip.description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for different sections */}
        <Tabs defaultValue={tabsToShow[0]} className="space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground min-w-full sm:w-full">
              {tabsToShow.includes('itinerary') && (
                <TabsTrigger 
                  value="itinerary" 
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Itinerario</span>
                  <span className="sm:hidden">Itiner.</span>
                </TabsTrigger>
              )}
              {tabsToShow.includes('cruise') && (
                <TabsTrigger 
                  value="cruise" 
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  <Ship className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Crociera</span>
                  <span className="sm:hidden">Cruise</span>
                </TabsTrigger>
              )}
              {user.role !== 'client' && (
                <TabsTrigger 
                  value="client-notes" 
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Note Clienti</span>
                  <span className="sm:hidden">Note</span>
                </TabsTrigger>
              )}
              {tabsToShow.includes('trip-details') && (
                <TabsTrigger 
                  value="trip-details" 
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  <Info className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Dettagli Viaggio</span>
                  <span className="sm:hidden">Dettagli</span>
                </TabsTrigger>
              )}
              {tabsToShow.includes('notes') && (
                <TabsTrigger 
                  value="notes" 
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap"
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Le Mie Note</span>
                  <span className="sm:hidden">Mie Note</span>
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="itinerary">
            <Card>
              <CardHeader>
                <CardTitle>Itinerario Giornaliero</CardTitle>
                <CardDescription>
                  Programma dettagliato del tuo viaggio
                </CardDescription>
              </CardHeader>
              <CardContent>
                {itineraries.length > 0 ? (
                  <div className="space-y-4">
                    {itineraries.map((itinerary) => (
                      <div key={itinerary.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start gap-4">
                          <div className="bg-teal-100 text-teal-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium">
                            {itinerary.day_number}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-slate-800">{itinerary.title}</h4>
                            <p className="text-sm text-slate-600 mb-2">
                              {new Date(itinerary.date).toLocaleDateString('it-IT')}
                            </p>
                            <p className="text-slate-600">{itinerary.description}</p>
                            <Badge variant="outline" className="mt-2">
                              {itinerary.itinerary_type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-slate-500">Itinerario non ancora disponibile</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {tabsToShow.includes('cruise') && cruiseInfo && (
            <TabsContent value="cruise">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ship className="w-5 h-5" />
                    Informazioni Crociera
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-slate-800">Nome Nave</h4>
                        <p className="text-slate-600">{cruiseInfo.ship_name}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800">Numero Cabina</h4>
                        <p className="text-slate-600">{cruiseInfo.cabin_number}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-slate-800">Orario Partenza</h4>
                        <p className="text-slate-600">
                          {new Date(cruiseInfo.departure_time).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800">Orario Rientro</h4>
                        <p className="text-slate-600">
                          {new Date(cruiseInfo.return_time).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {cruiseInfo.ship_facilities.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="font-medium text-slate-800 mb-3">Servizi a Bordo</h4>
                      <div className="flex flex-wrap gap-2">
                        {cruiseInfo.ship_facilities.map((facility, index) => (
                          <Badge key={index} variant="outline">
                            {facility}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {tabsToShow.includes('notes') && (
            <TabsContent value="notes">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Le Mie Note Personali
                    </span>
                    <Button 
                      onClick={() => setShowNewNoteDialog(true)}
                      size="sm" 
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Aggiungi Nota
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Scrivi qui i tuoi pensieri e ricordi del viaggio
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {notes.filter(note => !note.note_text.startsWith('TRIP_DETAILS:')).length > 0 ? (
                    <div className="space-y-4">
                      {notes.filter(note => !note.note_text.startsWith('TRIP_DETAILS:')).map((note) => (
                        <div key={note.id} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {note.day_number > 0 && (
                                  <Badge variant="outline">Giorno {note.day_number}</Badge>
                                )}
                                <span className="text-xs text-slate-500">
                                  {new Date(note.created_at).toLocaleDateString('it-IT')}
                                </span>
                              </div>
                              <p className="text-slate-600">{note.note_text}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => setEditingNote(note)}
                              className="flex items-center gap-2"
                            >
                              <Edit className="w-4 h-4" />
                              Modifica
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                      <p className="text-slate-500 mb-4">Nessuna nota ancora scritta</p>
                      <Button onClick={() => setShowNewNoteDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Scrivi la tua prima nota
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Client Notes Tab - For Admin/Agent to see client notes */}
          {user.role !== 'client' && (
            <TabsContent value="client-notes">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Note del Cliente
                  </CardTitle>
                  <CardDescription>
                    Note e commenti inviati dal cliente per questo viaggio
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {notes && notes.filter(note => !note.note_text.startsWith('TRIP_DETAILS:')).length > 0 ? (
                    <div className="space-y-4">
                      {notes.filter(note => !note.note_text.startsWith('TRIP_DETAILS:')).map((note) => (
                        <div key={note.id} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {note.day_number > 0 && (
                                <Badge variant="outline">Giorno {note.day_number}</Badge>
                              )}
                              <span className="text-sm text-slate-500">
                                {new Date(note.created_at).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                          </div>
                          <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">
                            {note.note_text}
                          </p>
                          {note.updated_at && note.updated_at !== note.created_at && (
                            <p className="text-xs text-slate-500 mt-2">
                              Modificato il: {new Date(note.updated_at).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                      <h4 className="text-lg font-medium text-slate-800 mb-2">Nessuna nota dal cliente</h4>
                      <p className="text-slate-500">
                        Il cliente non ha ancora lasciato note per questo viaggio
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Trip Details Tab - For all users (read-only for clients) */}
          {tabsToShow.includes('trip-details') && (
            <TabsContent value="trip-details">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Informazioni Dettagliate
                  </CardTitle>
                  <CardDescription>
                    {user.role === 'client' 
                      ? 'Dettagli del tuo viaggio compilati dal tuo agente'
                      : 'Dettagli specifici da fornire al cliente'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Cruise Information */}
                    {trip.trip_type === 'cruise' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                          <Ship className="w-5 h-5" />
                          Informazioni Crociera
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Nome Nave</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.ship_name || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: MSC Seaside" 
                                value={tripDetailsForm.ship_name}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, ship_name: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Porto di Imbarco</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.boarding_port || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: Civitavecchia" 
                                value={tripDetailsForm.boarding_port}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, boarding_port: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Numero Cabina</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.cabin_number || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: 7145" 
                                value={tripDetailsForm.cabin_number}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, cabin_number: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Tipo Pacchetto</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.package_type || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: Balcone Premium" 
                                value={tripDetailsForm.package_type}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, package_type: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Tipo Assicurazione</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.insurance_type || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: Annullamento + Medica" 
                                value={tripDetailsForm.insurance_type}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, insurance_type: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Ristorante</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.restaurant || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: Ristorante Principale" 
                                value={tripDetailsForm.restaurant}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, restaurant: e.target.value})}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">Turno Cena</label>
                            {user.role === 'client' ? (
                              <div className="bg-white p-2 border border-blue-300 rounded-md text-blue-800">
                                {tripDetailsForm.dinner_time || 'Non specificato'}
                              </div>
                            ) : (
                              <Input 
                                placeholder="Es: Secondo turno - 21:00" 
                                value={tripDetailsForm.dinner_time}
                                onChange={(e) => setTripDetailsForm({...tripDetailsForm, dinner_time: e.target.value})}
                              />
                            )}
                          </div>
                        </div>
                        {user.role !== 'client' && (
                          <Button 
                            className="mt-4" 
                            onClick={handleSaveTripDetails}
                            disabled={false}
                            type="button"
                          >
                            Salva Informazioni Crociera
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Resort Information */}
                    {trip.trip_type === 'resort' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                          <MapPin className="w-5 h-5" />
                          Informazioni Resort
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">Nome Resort</label>
                            <Input 
                              placeholder="Es: Sandals Royal Caribbean" 
                              value={tripDetailsForm.resort_name}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, resort_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">Tipologia Camera</label>
                            <Input 
                              placeholder="Es: Junior Suite Vista Mare" 
                              value={tripDetailsForm.room_type}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, room_type: e.target.value})}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">Pasto Entrata</label>
                            <Input 
                              placeholder="Es: All Inclusive" 
                              value={tripDetailsForm.meal_plan}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, meal_plan: e.target.value})}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">Formula</label>
                            <Input 
                              placeholder="Es: All Inclusive Premium" 
                              value={tripDetailsForm.package_formula}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, package_formula: e.target.value})}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-green-700 mb-1">Assicurazione</label>
                            <Input 
                              placeholder="Es: Annullamento + Medica + Bagaglio" 
                              value={tripDetailsForm.insurance_type}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, insurance_type: e.target.value})}
                            />
                          </div>
                        </div>
                        <Button 
                          className="mt-4" 
                          onClick={handleSaveTripDetails}
                          disabled={false}
                          type="button"
                        >
                          Salva Informazioni Resort
                        </Button>
                      </div>
                    )}

                    {/* Tour Information */}
                    {trip.trip_type === 'tour' && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Informazioni Tour
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-purple-700 mb-1">Informazioni Generali</label>
                            <Textarea 
                              placeholder="Inserisci tutte le informazioni generali del tour: guida, trasporti, hotel, pasti inclusi, punti di interesse, documenti necessari, etc."
                              rows={6}
                              value={tripDetailsForm.general_info}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, general_info: e.target.value})}
                            />
                          </div>
                        </div>
                        <Button 
                          className="mt-4" 
                          onClick={handleSaveTripDetails}
                          disabled={false}
                          type="button"
                        >
                          Salva Informazioni Tour
                        </Button>
                      </div>
                    )}

                    {/* Custom trip or other types */}
                    {(!trip.trip_type || trip.trip_type === 'custom') && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Info className="w-5 h-5" />
                          Informazioni Personalizzate
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Dettagli del Viaggio</label>
                            <Textarea 
                              placeholder="Inserisci tutti i dettagli specifici per questo viaggio personalizzato..."
                              rows={4}
                              value={tripDetailsForm.custom_details}
                              onChange={(e) => setTripDetailsForm({...tripDetailsForm, custom_details: e.target.value})}
                            />
                          </div>
                        </div>
                        <Button 
                          className="mt-4" 
                          onClick={handleSaveTripDetails}
                          disabled={false}
                          type="button"
                        >
                          Salva Informazioni
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

        </Tabs>

        {/* Note Dialog */}
        <Dialog open={showNewNoteDialog || !!editingNote} onOpenChange={() => {
          setShowNewNoteDialog(false);
          setEditingNote(null);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingNote ? 'Modifica Nota' : 'Nuova Nota'}
              </DialogTitle>
              <DialogDescription>
                Scrivi i tuoi pensieri e ricordi del viaggio
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="day_number">Giorno del viaggio</Label>
                <Input
                  id="day_number"
                  type="number"
                  min="1"
                  value={editingNote ? editingNote.day_number : newNote.day_number}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (editingNote) {
                      setEditingNote({...editingNote, day_number: value});
                    } else {
                      setNewNote({...newNote, day_number: value});
                    }
                  }}
                />
              </div>
              
              <div>
                <Label htmlFor="note_text">Il tuo ricordo</Label>
                <Textarea
                  id="note_text"
                  value={editingNote ? editingNote.note_text : newNote.note_text}
                  onChange={(e) => {
                    if (editingNote) {
                      setEditingNote({...editingNote, note_text: e.target.value});
                    } else {
                      setNewNote({...newNote, note_text: e.target.value});
                    }
                  }}
                  placeholder="Racconta la tua esperienza di oggi..."
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowNewNoteDialog(false);
                setEditingNote(null);
              }}>
                Annulla
              </Button>
              <Button onClick={() => handleSaveNote(editingNote || newNote)}>
                <Save className="w-4 h-4 mr-2" />
                Salva Nota
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cambia Stato Viaggio</DialogTitle>
              <DialogDescription>
                Seleziona il nuovo stato per questo viaggio. 
                {trip.status === 'draft' && ' Puoi confermare il viaggio per renderlo ufficiale.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="status">Nuovo Stato</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (Bozza)</SelectItem>
                    <SelectItem value="active">Active (Attivo)</SelectItem>
                    <SelectItem value="confirmed">Confirmed (Confermato)</SelectItem>
                    <SelectItem value="completed">Completed (Completato)</SelectItem>
                    <SelectItem value="cancelled">Cancelled (Annullato)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newStatus === 'confirmed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    ✅ Confermando il viaggio, questo diventerà ufficiale e apparirà nei report finanziari.
                  </p>
                </div>
              )}

              {newStatus === 'cancelled' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    ⚠️ Annullando il viaggio, questo non sarà più disponibile per i clienti.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleStatusChange} disabled={!newStatus || newStatus === trip.status}>
                <Settings className="w-4 h-4 mr-2" />
                Aggiorna Stato
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default TripView;